import { useMemo, useState } from "react";
import { GiBroadsword, GiCheckedShield, GiHeartPlus, GiSandsOfTime } from "react-icons/gi";
import { UnitCatalogEntry, UnitClassRu } from "../types/army";
import { ResourcePool } from "../types/castle";
import { formatDuration, formatNumber } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { CurrencyValue } from "./CurrencyIcon";
import { AnimatedUnitIcon } from "./AnimatedUnitIcon";
import { UnitDescriptions } from "./unitDescriptions";
import {
  TIER_MAX,
  TIER_MIN,
  maxAllowedTier,
  maxBatchForBuildingLevel,
  requiredBuildingLevelForTier,
  tierCostMultiplier,
  tierName,
  tierStatMultiplier,
  tierTimeMultiplier,
} from "../lib/garrisonKey";

interface Props {
  unit: UnitCatalogEntry;
  resources: ResourcePool;
  trainSpeedReductionPct: number;
  // Level of the training building (Barracks / ArcheryRange / MageTower /
  // SignatureForge) the unit comes from. Drives which tiers can be picked.
  buildingLevel: number;
  onClose: () => void;
  onConfirm: (count: number, tier: number) => void;
  pending: boolean;
  error: string | null;
}

const presets = [10, 50, 100, 500];

function affordableMax(
  unit: UnitCatalogEntry,
  resources: ResourcePool,
  costMult: number,
): number {
  const { costPerUnit } = unit;
  const need = (v: number) => Math.ceil(v * costMult);
  const cap = (have: number, perUnit: number) =>
    perUnit <= 0 ? Number.POSITIVE_INFINITY : Math.floor(have / perUnit);
  return Math.min(
    cap(resources.gold, need(costPerUnit.gold)),
    cap(resources.wood, need(costPerUnit.wood)),
    cap(resources.stone, need(costPerUnit.stone)),
    cap(resources.mana, need(costPerUnit.mana)),
  );
}

export function RecruitModal({
  unit,
  resources,
  trainSpeedReductionPct,
  buildingLevel,
  onClose,
  onConfirm,
  pending,
  error,
}: Props) {
  const allowedTier = Math.max(TIER_MIN, maxAllowedTier(buildingLevel));
  const [tier, setTier] = useState<number>(allowedTier);
  // If the player downgrades the training building before opening the modal,
  // snap tier back into legal range.
  const effectiveTier = useMemo(
    () => Math.min(Math.max(TIER_MIN, tier), allowedTier),
    [tier, allowedTier],
  );

  const costMult = tierCostMultiplier(effectiveTier);
  const timeMult = tierTimeMultiplier(effectiveTier);
  const statMult = tierStatMultiplier(effectiveTier);

  const maxAfford = affordableMax(unit, resources, costMult);
  // Server-side cap from CastleService.MaxBatchForLevel: a single training
  // order can request at most `buildingLevel × 350` units. UI honors the
  // same ceiling so «Макс» and presets never suggest a value the server
  // would reject with `BatchTooLarge`.
  const maxBatch = maxBatchForBuildingLevel(buildingLevel);
  const maxAllowed = Math.min(maxAfford, maxBatch);
  const cappedByBatch = maxBatch > 0 && maxBatch < maxAfford;
  const [count, setCount] = useState<number>(() => Math.min(10, Math.max(1, maxAllowed)));

  const perUnitCost = {
    gold: Math.ceil(unit.costPerUnit.gold * costMult),
    wood: Math.ceil(unit.costPerUnit.wood * costMult),
    stone: Math.ceil(unit.costPerUnit.stone * costMult),
    mana: Math.ceil(unit.costPerUnit.mana * costMult),
  };
  const totalCost = {
    gold: perUnitCost.gold * count,
    wood: perUnitCost.wood * count,
    stone: perUnitCost.stone * count,
    mana: perUnitCost.mana * count,
  };
  const totalSeconds = Math.ceil(
    unit.timePerUnitSeconds * count * timeMult * (1 - Math.min(trainSpeedReductionPct, 0.9)),
  );
  const canSubmit = count > 0 && count <= maxAllowed;

  // Allow the field to be temporarily cleared (count=0) while the player is
  // typing a new multi-digit number. canSubmit (`count > 0`) prevents the
  // request from going out at that interim 0.
  const setSafe = (n: number) => {
    if (!Number.isFinite(n) || n <= 0) {
      setCount(0);
      return;
    }
    const v = Math.min(Math.floor(n), Math.max(1, maxAllowed));
    setCount(v);
  };

  return (
    <div
      className="fixed inset-0 z-10 flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-2xl bg-tg-bg p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-tg-secondaryBg">
              <AnimatedUnitIcon
                unitId={unit.id}
                cls={unit.cls}
                element={unit.element}
                size={64}
                rounded="xl"
              />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-semibold leading-tight">{unit.name}</div>
              <div className="mt-0.5 text-xs text-tg-hint">{UnitClassRu[unit.cls]}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-tg-hint"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        {UnitDescriptions[unit.id] && (
          <div className="mb-3 rounded-lg bg-tg-secondaryBg p-3 text-sm leading-relaxed text-tg-text/90">
            {UnitDescriptions[unit.id]}
          </div>
        )}

        <div className="mb-2 flex items-center justify-between text-xs text-tg-hint">
          <span>Тир</span>
          <span className="font-medium text-tg-text">
            {tierName(effectiveTier)} · T{effectiveTier}
          </span>
        </div>
        <div className="mb-3 flex flex-wrap gap-1">
          {Array.from({ length: TIER_MAX }, (_, i) => i + 1).map((t) => {
            const locked = t > allowedTier;
            const selected = t === effectiveTier;
            const reqLvl = requiredBuildingLevelForTier(t);
            return (
              <button
                key={t}
                type="button"
                disabled={locked}
                onClick={() => {
                  hapticImpact("light");
                  setTier(t);
                }}
                title={locked ? `Требуется здание Lvl ${reqLvl}` : `T${t} · ${tierName(t)}`}
                className={
                  "rounded-md px-2 py-1 text-xs tabular-nums " +
                  (selected
                    ? "bg-tg-button text-tg-buttonText"
                    : locked
                    ? "bg-tg-secondaryBg text-tg-hint opacity-40"
                    : "bg-tg-secondaryBg text-tg-text")
                }
              >
                T{t}
              </button>
            );
          })}
        </div>
        {effectiveTier < TIER_MAX && (
          <div className="mb-3 text-[11px] text-tg-hint">
            Следующий тир — здание Lvl {requiredBuildingLevelForTier(effectiveTier + 1)}.
          </div>
        )}

        <div className="mb-3 text-xs text-tg-hint">
          <span className="inline-flex items-center gap-1">
            <GiHeartPlus size={12} /> {Math.round(unit.hp * statMult)}
          </span>
          {" · "}
          <span className="inline-flex items-center gap-1">
            <GiBroadsword size={12} /> {Math.round(unit.attack * statMult)}
          </span>
          {" · "}
          <span className="inline-flex items-center gap-1">
            <GiCheckedShield size={12} /> {Math.round(unit.defense * statMult)}
          </span>
          {" · "}
          <span className="inline-flex items-center gap-1">
            <GiSandsOfTime size={12} /> {formatDuration(Math.ceil(unit.timePerUnitSeconds * timeMult))}/шт
          </span>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              disabled={p > maxAllowed}
              onClick={() => {
                hapticImpact("light");
                setSafe(p);
              }}
              className="rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm disabled:opacity-30"
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={maxAllowed <= 0}
            onClick={() => {
              hapticImpact("light");
              setSafe(maxAllowed);
            }}
            className="rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm disabled:opacity-30"
          >
            Макс {maxAllowed > 0 ? `(${formatNumber(maxAllowed)})` : ""}
          </button>
        </div>
        {cappedByBatch && (
          <div className="mb-2 text-[11px] text-tg-hint">
            Лимит партии — {formatNumber(maxBatch)} за один заказ (здание Lvl {buildingLevel} × 350). Поднимай здание, чтобы тренировать больше за раз.
          </div>
        )}

        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={maxAllowed || 1}
          value={count > 0 ? count : ""}
          placeholder={maxAllowed > 0 ? `до ${maxAllowed}` : "0"}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setSafe(0);
              return;
            }
            setSafe(parseInt(raw, 10));
          }}
          className="mb-3 w-full rounded-lg bg-tg-secondaryBg p-2 text-center text-lg tabular-nums"
        />

        <div className="mb-3 grid grid-cols-2 gap-1 text-sm text-tg-hint">
          <CurrencyValue kind="gold" value={formatNumber(totalCost.gold)} size={16} />
          <CurrencyValue kind="wood" value={formatNumber(totalCost.wood)} size={16} />
          <CurrencyValue kind="stone" value={formatNumber(totalCost.stone)} size={16} />
          <CurrencyValue kind="mana" value={formatNumber(totalCost.mana)} size={16} />
          <div className="col-span-2 pt-1 text-tg-text">⏱ {formatDuration(totalSeconds)}</div>
        </div>

        {error ? (
          <div className="mb-2 text-sm text-tg-destructive">{error}</div>
        ) : null}

        <button
          type="button"
          disabled={!canSubmit || pending}
          onClick={() => {
            hapticImpact("medium");
            onConfirm(count, effectiveTier);
            hapticNotification("success");
          }}
          className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
        >
          {pending ? "Запускаю…" : `Нанять T${effectiveTier}`}
        </button>
      </div>
    </div>
  );
}
